import React, { FC, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { Case, Default, Switch, When } from 'react-if'
import { Button, LinkButton } from '@/atoms/Button'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { PlaySquareIcon } from '@/atoms/Icons/PlaySquareIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { Image } from '@/atoms/Image'
import { LabelMD, ParagraphSM, TitleMD } from '@/atoms/Text'
import { paths } from '@/constants'
import { Modal } from '@/molecules/Modal'
import { CatalogTitle } from '@/services/ContentCatalog'
import { canStreamEpisode } from '@/services/FranchiseService'
import { Episode, Project } from '@/services/ProjectsService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface FranchiseFilmSeriesCTAProps {
  episode: Episode
  project: Project | undefined
  isGuildMember?: boolean
  hasWatchableReasons?: boolean
  catalogTitle?: CatalogTitle
}

export const FranchiseFilmSeriesCTA: FC<FranchiseFilmSeriesCTAProps> = React.memo(
  ({ episode, project, isGuildMember = false, hasWatchableReasons = false }) => {
    const { t } = useTranslate('watch')
    const track = useSafeTrack()
    const [showModal, setShowModal] = useState(true)

    const searchParams = useSearchParams()
    const hasPifSearchParams = searchParams?.has('homestead-pif') ? searchParams?.get('homestead-pif') : null

    if (hasWatchableReasons || (isGuildMember && hasPifSearchParams)) {
      return null
    }

    const handleClick = (variant: string) => {
      switch (variant) {
        case 'getShowtimes':
          track('Clicked Franchise Film CTA Get Showtimes', {
            guid: episode?.guid,
            episode,
            project,
          })
          break
        case 'watchSeries':
          track('Clicked Franchise Film CTA Watch Series', {
            guid: episode?.guid,
            episode,
            project,
          })
          break
        case 'joinGuild':
          track('Clicked Franchise Film CTA Join Guild', {
            guid: episode?.guid,
            episode,
            project,
          })
          break
      }
      handleModalClose()
    }

    const handleModalClose = () => {
      setShowModal(false)
    }

    const movieTitle = project?.name
    // hardcoding image urls for now to be for movie and not series
    const portraitImageUrl = 'v1730220132/studio-app/catalog/b47bb274-6aa4-4bf8-b731-28b9efea4a45'
    const landscapeImageUrl = 'v1730220298/studio-app/catalog/4297c379-a0bc-47b3-ac0e-8420ec876963'
    const ticketsLink = project?.slug ? `${paths.tickets.index}/${project.slug}` : paths.tickets.index
    const guildLink = project?.slug ? `${paths.guild.join}/${project.slug}` : paths.guild.join
    const canStream = canStreamEpisode(isGuildMember, episode)

    return (
      <Modal
        panelClasses="!p-4 bg-white text-black !max-w-[430px] lg:!max-w-[735px]"
        isOpen={showModal}
        onClose={handleModalClose}
      >
        <div className="mt-4 flex flex-col gap-4 lg:mt-0 lg:flex-row lg:gap-8 lg:px-4 lg:pb-4">
          <div className="w-full shrink-0 lg:w-fit">
            <Image
              src={getCloudinaryImageUrl({
                path: portraitImageUrl + '.webp',
                width: 224,
                height: 336,
                transforms: 'c_fill',
              })}
              alt={`${movieTitle} poster`}
              width={224}
              height={336}
              className="hidden rounded-xl lg:flex"
            />
            <Image
              src={getCloudinaryImageUrl({
                path: landscapeImageUrl + '.webp',
                width: 396,
                height: 222,
                transforms: 'c_fill',
              })}
              alt={`${movieTitle} poster`}
              width={396}
              height={222}
              className="flex max-w-full rounded-xl lg:hidden"
            />
          </div>

          <div className="flex flex-col gap-y-4">
            <Switch>
              <Case condition={isGuildMember}>
                <TitleMD weight="bold" className="lg:photon-heading-xs max-w-[415px]">
                  <Translate i18nKey="guildTitleWatchFilmSeries" t={t}>
                    Watch the Film before the Series!
                  </Translate>
                </TitleMD>
                <ParagraphSM>
                  <Translate i18nKey="homesteadLoyalGuildMemberWatchSeriesContinuationFilm" t={t}>
                    Watch Homestead: The Film in theaters before watching the series! The series is a continuation of
                    the film.
                  </Translate>
                </ParagraphSM>
              </Case>
              <Case condition={hasWatchableReasons}>
                <TitleMD weight="bold" className="lg:photon-heading-xs max-w-[415px]">
                  <Translate i18nKey="pifTitleThanksPayingForward" t={t}>
                    Thanks for Paying It Forward!
                  </Translate>
                </TitleMD>
                <ParagraphSM>
                  <Translate i18nKey="homesteadSeriesContinuationFilmPif" t={t}>
                    Your generosity helps bring inspiring stories like Homestead to others. Remember, Homestead: The
                    Series is a continuation of the film currently in theaters. Watch the film first to avoid spoilers,
                    then come back to enjoy the series.
                  </Translate>
                </ParagraphSM>
              </Case>
              <Default>
                <TitleMD weight="bold" className="lg:photon-heading-xs max-w-[415px]">
                  <Translate i18nKey="nonGuildTitleFreeTickets" t={t}>
                    Get 2 free tickets to Homestead and More!
                  </Translate>
                </TitleMD>
                <ParagraphSM>
                  <Translate i18nKey="homesteadNonGuildSeriesContinuationFilmJoinGuild" t={t}>
                    Homestead: The Series is a continuation of the film currently in theaters! Join the Guild today to
                    get 2 free tickets to watch the movie and unlock the entire series PLUS our full library of
                    inspiring entertainment.
                  </Translate>
                </ParagraphSM>
              </Default>
            </Switch>

            <div className="flex flex-col gap-4 lg:mt-4 lg:flex-row">
              <When condition={isGuildMember}>
                <Button
                  onClick={() => handleClick('watchSeries')}
                  variant="oxide-bright"
                  className="relative w-full overflow-hidden border-core-gray-300 bg-core-gray-300 px-[25px] py-[13px] text-core-gray-950 ring-0 ring-transparent lg:w-fit"
                >
                  <div className="relative flex min-h-6 flex-row items-center">
                    {canStream && <PlaySquareIcon color="black" size={20} />}
                    <LabelMD className="pl-1">
                      <When condition={canStream}>{t('watchSeries', 'Watch Series')}</When>
                      <When condition={!canStream}>{t('dismiss', 'Dismiss')}</When>
                    </LabelMD>
                  </div>
                </Button>
                <LinkButton
                  onClick={() => handleClick('getShowtimes')}
                  variant="gray-400"
                  href={ticketsLink}
                  className="w-full border-core-gray-300 bg-core-gray-300 px-[25px] py-[13px] text-core-gray-950 ring-0 ring-transparent lg:w-fit"
                >
                  <div className="flex min-h-6 flex-row items-center">
                    <TicketIcon color="black" size={20} />
                    <LabelMD className="pl-1">{t('getShowtimes', 'Get Showtimes')}</LabelMD>
                  </div>
                </LinkButton>
              </When>
              <When condition={!isGuildMember}>
                <LinkButton
                  onClick={() => handleClick('joinGuild')}
                  variant="oxide-bright"
                  href={guildLink}
                  className="w-full px-[25px] py-[13px] text-core-gray-950 ring-0 ring-transparent lg:w-fit"
                >
                  <div className="flex min-h-6 flex-row items-center">
                    <GuildIconMono color="black" size={20} />
                    <LabelMD className="pl-1">{t('joinTheGuildFranchise', 'Join the Guild')}</LabelMD>
                  </div>
                </LinkButton>
                <Button
                  onClick={() => setShowModal(false)}
                  variant="gray-400"
                  className="w-full border-core-gray-300 bg-core-gray-300 px-[25px] py-[13px] text-core-gray-950 ring-0 ring-transparent lg:w-fit"
                >
                  <div className="flex min-h-6 flex-row items-center">
                    <LabelMD>{t('dismiss', 'Dismiss')}</LabelMD>
                  </div>
                </Button>
              </When>
            </div>
          </div>
        </div>
      </Modal>
    )
  },
)

FranchiseFilmSeriesCTA.displayName = 'React.memo(FranchiseFilmSeriesCTA)'
